import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';
import { menuPdfUrls, newMenuPdfUrls } from '@/i18n/config';
import { useNewMenuLive } from '@/hooks/useNewMenuLive';
import { announce } from '@/lib/a11y';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import 'swiper/css/effect-coverflow';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const ZOOM_MAX_RATIO = 3;
/** Stay well below the ~16.7M pixel canvas limit of iOS Safari. */
const MAX_CANVAS_PIXELS = 12_000_000;
const PAGE_ASPECT_RATIO = 1.5;

const getPixelRatio = () => (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

const Menu = () => {
  const { t } = useTranslation(['menu', 'seo', 'common']);
  const { locale } = useLocale();
  const isNewMenu = useNewMenuLive();
  const pdfUrl = (isNewMenu ? newMenuPdfUrls : menuPdfUrls)[locale];
  const [numPages, setNumPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pageWidth, setPageWidth] = useState(600);
  const [pdfError, setPdfError] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pixelRatio, setPixelRatio] = useState(getPixelRatio);

  const sections = t('menu:structuredData.sections', { returnObjects: true }) as string[];

  const zoomedPixelRatio = Math.min(
    pixelRatio * ZOOM_MAX_RATIO,
    Math.sqrt(MAX_CANVAS_PIXELS / (pageWidth * pageWidth * PAGE_ASPECT_RATIO)),
  );

  useEffect(() => {
    const updateWidth = () => {
      if (typeof window !== 'undefined') {
        setPageWidth(Math.min(window.innerWidth - 48, 600));
        setPixelRatio(getPixelRatio());
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setPdfError(false);
    setNumPages(0);
    setActivePage(1);
  }, [pdfUrl]);

  function onDocumentLoadSuccess({ numPages: total }: { numPages: number }) {
    setNumPages(total);
    setIsLoading(false);
    setPdfError(false);
    announce(t('menu:pdfLoaded', { count: total }));
  }

  function onDocumentLoadError() {
    setIsLoading(false);
    setPdfError(true);
  }

  const handleSlideChange = (swiper: SwiperType) => {
    const page = swiper.activeIndex + 1;
    setActivePage(page);
    if (numPages > 0) {
      announce(t('menu:pageAnnouncement', { current: page, total: numPages }));
    }
  };

  const menuStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: t('menu:structuredData.name'),
    description: t('menu:structuredData.description'),
    url: `https://braceesapori.it${locale === 'en' ? '/en' : ''}/menu`,
    hasMenuSection: sections.map((name) => ({ '@type': 'MenuSection', name })),
    inLanguage: locale,
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:menu.title')}
        description={t('seo:menu.description')}
        keywords={t('seo:menu.keywords')}
        ogImage="/img/DSC09190 (2024-04-22T17_54_40.682).JPG"
        logicalPath="/menu"
        noindex
        structuredData={menuStructuredData}
      />
      <Header />
      <section className="py-16 px-6 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 text-center">
            <h1 className="mb-4 text-4xl font-semibold text-foreground md:text-5xl">{t('menu:title')}</h1>
            <p className="mb-6 text-muted-foreground">{t('menu:subtitle')}</p>
          </div>

          <div
            role="region"
            aria-label={t('menu:viewerRegion')}
            aria-busy={isLoading}
            className="relative mt-8 overflow-hidden rounded-lg border border-border bg-card shadow-lg"
          >
            <div role="status" aria-live="polite" className="sr-only">
              {isLoading && t('menu:loading')}
              {!isLoading && numPages > 0 && activePage > 0 && t('menu:pageAnnouncement', { current: activePage, total: numPages })}
            </div>

            {pdfError ? (
              <div className="flex h-[400px] items-center justify-center p-8 text-center">
                <p className="text-muted-foreground">{t('menu:pdfComingSoon')}</p>
              </div>
            ) : (
              <>
                {isLoading && (
                  <div className="flex h-[600px] items-center justify-center md:h-[800px] lg:h-[900px]" aria-hidden="true">
                    <div className="text-center">
                      <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                      <p className="text-sm text-muted-foreground">{t('menu:loading')}</p>
                    </div>
                  </div>
                )}

                <div className="lg:hidden w-full max-w-full overflow-hidden">
                  {numPages > 0 && (
                    <p className="px-4 pt-4 text-center text-xs text-muted-foreground">{t('menu:zoomHint')}</p>
                  )}
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={null}
                    className="flex justify-center w-full"
                  >
                    {numPages > 0 && (
                      <Swiper
                        modules={[Navigation, Pagination, Zoom]}
                        spaceBetween={20}
                        slidesPerView={1}
                        zoom={{ maxRatio: ZOOM_MAX_RATIO, toggle: true }}
                        pagination={{ clickable: true, dynamicBullets: true }}
                        className="!pb-12 !w-full !max-w-full"
                        style={{ width: '100%', maxWidth: '100%' }}
                        aria-roledescription="carousel"
                        aria-label={t('menu:carouselLabel')}
                        onSlideChange={handleSlideChange}
                      >
                        {Array.from(new Array(numPages), (_, index) => (
                          <SwiperSlide key={`page_${index + 1}`} className="!w-full" aria-label={t('menu:slideLabel', { page: index + 1, total: numPages })}>
                            <div className="swiper-zoom-container px-4 w-full">
                              <Page
                                pageNumber={index + 1}
                                width={pageWidth}
                                devicePixelRatio={index === activePage - 1 ? zoomedPixelRatio : pixelRatio}
                                renderTextLayer
                                renderAnnotationLayer
                                className="swiper-zoom-target shadow-lg max-w-full"
                              />
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    )}
                  </Document>
                </div>

                <div className="hidden lg:block">
                  <div className="max-h-[900px] overflow-y-auto p-6">
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={null}
                      className="flex flex-col items-center gap-6"
                    >
                      {Array.from(new Array(numPages), (_, index) => (
                        <div key={`page_${index + 1}`} className="flex justify-center" aria-label={t('menu:slideLabel', { page: index + 1, total: numPages })}>
                          <Page
                            pageNumber={index + 1}
                            width={900}
                            devicePixelRatio={pixelRatio}
                            renderTextLayer
                            renderAnnotationLayer
                            className="shadow-lg"
                          />
                        </div>
                      ))}
                    </Document>
                  </div>
                </div>
              </>
            )}
          </div>

          {!pdfError && (
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row mt-5">
              <Button variant="ghost" size="lg" asChild>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  {t('common:cta.openNewTab')}
                </a>
              </Button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default Menu;
